import { apiFetch } from '../../../lib/api';
import {
    getMySchoolCached,
    setMySchoolCache,
    notifyMySchoolUpdated,
} from '../../../lib/mySchoolCache';

/**
 * List schools with optional filters and pagination.
 * @param {Object} params - Query parameters
 * @param {boolean|null} [params.is_active] - Filter by active status
 * @param {string|null} [params.q] - Case-insensitive search query
 * @param {number} [params.skip=0] - Number of records to skip
 * @param {number} [params.limit=50] - Number of records to return
 * @param {string} [token] - Optional access token
 */
export const getSchools = async (params = {}, token) => {
    const searchParams = new URLSearchParams();
    
    if (params.skip !== undefined && params.skip !== null) {
        searchParams.append('skip', params.skip);
    }
    if (params.limit !== undefined && params.limit !== null) {
        searchParams.append('limit', params.limit);
    }
    if (params.q !== undefined && params.q !== null) {
        searchParams.append('q', params.q);
    }
    if (params.is_active !== undefined && params.is_active !== null) {
        searchParams.append('is_active', params.is_active);
    }
    
    const queryString = searchParams.toString();
    const endpoint = queryString ? `/api/v1/schools?${queryString}` : '/api/v1/schools';
    
    return apiFetch(endpoint, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
};

/**
 * Get details of a specific school.
 * @param {string} schoolId - School UUID
 * @param {string} [token] - Optional access token
 */
export const getSchoolById = async (schoolId, token) => {
    return apiFetch(`/api/v1/schools/${schoolId}`, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
};

/**
 * Create a new school.
 * @param {Object} schoolData - School details
 * @param {string} schoolData.name - School name
 * @param {string} [schoolData.name_ar] - School name in Arabic
 * @param {string} [schoolData.code] - School code
 * @param {string} [schoolData.description] - School description
 * @param {string} [schoolData.address] - School address
 * @param {string} [schoolData.city] - School city
 * @param {string} [schoolData.country] - School country
 * @param {string} [schoolData.postal_code] - School postal code
 * @param {string} [schoolData.phone] - School phone number
 * @param {string} [schoolData.email] - School email address
 * @param {string} [schoolData.website_url] - School website URL
 * @param {string} [schoolData.logo_url] - School logo URL
 * @param {string} [schoolData.principal_name] - Principal/Director name
 * @param {string} [schoolData.owner_user_id] - Link after create via PATCH if not accepted on POST
 * @param {string} [token] - Optional access token
 */
export const createSchool = async (schoolData, token) => {
    return apiFetch('/api/v1/schools', {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: JSON.stringify(schoolData),
    });
};

/**
 * Update an existing school's details.
 * @param {string} schoolId - School UUID
 * @param {Object} schoolData - Updated school details
 * @param {string} [schoolData.name] - School name
 * @param {string} [schoolData.name_ar] - School name in Arabic
 * @param {string} [schoolData.code] - School code
 * @param {string} [schoolData.description] - School description
 * @param {string} [schoolData.address] - School address
 * @param {string} [schoolData.city] - School city
 * @param {string} [schoolData.country] - School country
 * @param {string} [schoolData.postal_code] - School postal code
 * @param {string} [schoolData.phone] - School phone number
 * @param {string} [schoolData.email] - School email address
 * @param {string} [schoolData.website_url] - School website URL
 * @param {string} [schoolData.logo_url] - School logo URL
 * @param {string} [schoolData.principal_name] - Principal/Director name
 * @param {boolean} [schoolData.is_active] - Active status
 * @param {string} [schoolData.owner_user_id] - Owner user UUID
 * @param {string} [token] - Optional access token
 */
export const updateSchool = async (schoolId, schoolData, token) => {
    return apiFetch(`/api/v1/schools/${schoolId}`, {
        method: 'PATCH',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: JSON.stringify(schoolData),
    });
};

/**
 * Delete a school.
 * @param {string} schoolId - School UUID
 * @param {string} [token] - Optional access token
 */
export const deleteSchool = async (schoolId, token) => {
    return apiFetch(`/api/v1/schools/${schoolId}`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
};

/** True when GET /api/v1/users/me/school returned a linked school record. */
export function isSchoolProfile(data) {
    return Boolean(data && typeof data === 'object' && data.id);
}

function isNoSchoolProfileError(err) {
    if (!err) return false;
    if (err.status === 404 || err.status === 403) return true;
    const code = String(err?.data?.error || err?.data?.code || err?.message || '').toUpperCase();
    return code.includes('ROLE_REQUIRED') || code.includes('NOT_FOUND');
}

async function fetchTeacherSchoolFromApi(token) {
    try {
        const data = await apiFetch('/api/v1/teachers/me', {
            method: 'GET',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const school = data?.school;
        return isSchoolProfile(school) ? school : null;
    } catch (err) {
        if (isNoSchoolProfileError(err)) return null;
        throw err;
    }
}

async function fetchMySchoolFromApi(token, role) {
    if (role === 'teacher') {
        return fetchTeacherSchoolFromApi(token);
    }
    try {
        const data = await apiFetch('/api/v1/users/me/school', {
            method: 'GET',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        return isSchoolProfile(data) ? data : null;
    } catch (err) {
        if (isNoSchoolProfileError(err)) {
            return fetchTeacherSchoolFromApi(token);
        }
        throw err;
    }
}

/**
 * Get current user's school (cached per token; dedupes in-flight requests).
 * @param {string} [token] - Optional access token
 * @param {{ refresh?: boolean, role?: string }} [options] - Pass refresh: true to bypass cache
 */
export const getMySchool = async (token, options = {}) => {
    if (!token) return null;
    return getMySchoolCached(token, () => fetchMySchoolFromApi(token, options.role), options);
};

/**
 * Update current user's school.
 * @param {Object} schoolData - Updated school details
 * @param {string} [schoolData.name] - School name
 * @param {string} [schoolData.name_ar] - School name in Arabic
 * @param {string} [schoolData.code] - School code
 * @param {string} [schoolData.description] - School description
 * @param {string} [schoolData.address] - School address
 * @param {string} [schoolData.city] - School city
 * @param {string} [schoolData.country] - School country
 * @param {string} [schoolData.postal_code] - School postal code
 * @param {string} [schoolData.phone] - School phone number
 * @param {string} [schoolData.email] - School email address
 * @param {string} [schoolData.website_url] - School website URL
 * @param {string} [schoolData.logo_url] - School logo URL
 * @param {string} [schoolData.principal_name] - Principal/Director name
 * @param {string} [token] - Optional access token
 */
export const updateMySchool = async (schoolData, token) => {
    const data = await apiFetch('/api/v1/users/me/school', {
        method: 'PATCH',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: JSON.stringify(schoolData),
    });
    const normalized = isSchoolProfile(data) ? data : null;
    if (token) {
        setMySchoolCache(token, normalized);
        notifyMySchoolUpdated(normalized);
    }
    return normalized;
};

/**
 * List school users by role.
 * @param {string} schoolId - School UUID
 * @param {Object} [params] - Query parameters
 * @param {boolean} [params.include_inactive] - Include deactivated users/staff (default false on API)
 * @param {boolean} [params.active_enrollments_only] - Only active student enrollments (default true on API)
 * @param {boolean} [params.require_school_active] - false allows access when school is inactive
 * @param {number} [params.skip]
 * @param {number} [params.limit] - Max per role slice (default 500, max 1000)
 * @param {string} [params.role] - Optional: students | teachers | parents
 * @param {string} [token] - Optional access token
 */
export const getSchoolUsers = async (schoolId, params = {}, token) => {
    const searchParams = new URLSearchParams();
    if (params.include_inactive !== undefined) searchParams.append('include_inactive', params.include_inactive);
    if (params.active_enrollments_only !== undefined) searchParams.append('active_enrollments_only', params.active_enrollments_only);
    if (params.skip !== undefined) searchParams.append('skip', params.skip);
    if (params.limit !== undefined) searchParams.append('limit', params.limit);
    if (params.role) searchParams.append('role', params.role);
    if (params.is_active !== undefined) searchParams.append('is_active', params.is_active);
    if (params.require_school_active !== undefined) {
        searchParams.append('require_school_active', params.require_school_active);
    }

    const queryString = searchParams.toString();
    const endpoint = queryString ? `/api/v1/schools/${schoolId}/users?${queryString}` : `/api/v1/schools/${schoolId}/users`;

    return apiFetch(endpoint, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
};

export { bulkRemoveSchoolUsers, bulkRemoveSchoolUsersChunked } from './schoolUsersImportActions';

/**
 * Validate adding a new school user.
 * @param {string} schoolId
 * @param {Object} data
 * @param {string} [token]
 */
export const validateAddSchoolUser = async (schoolId, data, token) => {
    return apiFetch(`/api/v1/schools/${schoolId}/users/add/validate`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: JSON.stringify(data),
    });
};

/**
 * Add a new school user (unified POST endpoint with invites).
 * @param {string} schoolId
 * @param {Object} data
 * @param {string} [token]
 */
export const addSchoolUser = async (schoolId, data, token) => {
    return apiFetch(`/api/v1/schools/${schoolId}/users/add`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: JSON.stringify(data),
    });
};

/**
 * Add a new school family (parent + children).
 * @param {string} schoolId
 * @param {Object} data
 * @param {string} [token]
 */
export const addSchoolUserFamily = async (schoolId, data, token) => {
    return apiFetch(`/api/v1/schools/${schoolId}/users/add-family`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: JSON.stringify(data),
    });
};

/**
 * Get school invitations.
 * @param {string} schoolId
 * @param {Object} [params]
 * @param {string} [token]
 */
export const getSchoolInvitations = async (schoolId, params = {}, token) => {
    const searchParams = new URLSearchParams();
    if (params.skip !== undefined) searchParams.append('skip', params.skip);
    if (params.limit !== undefined) searchParams.append('limit', params.limit);
    if (params.role) searchParams.append('role', params.role);
    if (params.status) searchParams.append('status', params.status);

    const queryString = searchParams.toString();
    const endpoint = queryString ? `/api/v1/schools/${schoolId}/invitations?${queryString}` : `/api/v1/schools/${schoolId}/invitations`;

    return apiFetch(endpoint, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
};

/**
 * Revoke a school invitation.
 * @param {string} schoolId
 * @param {string} invitationId
 * @param {string} [token]
 */
export const revokeSchoolInvitation = async (schoolId, invitationId, token) => {
    return apiFetch(`/api/v1/schools/${schoolId}/invitations/${invitationId}`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
};

/**
 * Resend a school invitation.
 * @param {string} schoolId
 * @param {string} invitationId
 * @param {object} payload
 * @param {string} [token]
 */
export const resendSchoolInvitation = async (schoolId, invitationId, payload, token) => {
    return apiFetch(`/api/v1/schools/${schoolId}/invitations/${invitationId}/resend`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: JSON.stringify(payload),
    });
};

/**
 * Update a school directory user (unified PATCH for all roles).
 * PATCH /api/v1/schools/{school_id}/users/{user_id}
 *
 * @param {string} schoolId - School UUID
 * @param {string} userId   - Target user UUID
 * @param {Object} data     - SchoolDirectoryUserUpdate
 * @param {('student'|'teacher'|'admin'|'parent')} data.role - Required role discriminator
 * @param {string|null} [data.full_name]
 * @param {string|null} [data.email]
 * @param {boolean|null} [data.is_active]
 * @param {string|null} [data.grade_id]
 * @param {('active'|'withdrawn'|'graduated'|'transferred'|null)} [data.status] - student enrollment status
 * @param {('teacher'|'admin'|'principal'|'counselor'|'staff'|null)} [data.staff_role]
 * @param {{ requireSchoolActive?: boolean, token?: string }} [options]
 * @returns {Promise<Object>} SchoolDirectoryUserItem
 */
const ALLOWED_DIRECTORY_USER_UPDATE_FIELDS = new Set([
    'role',
    'grade_id',
    'status',
    'staff_role',
    'is_active',
    'full_name',
    'email',
]);

export const updateSchoolDirectoryUser = async (
    schoolId,
    userId,
    data,
    { requireSchoolActive, token } = {},
) => {
    const query = requireSchoolActive === false ? '?require_school_active=false' : '';
    const cleanedData = Object.fromEntries(
        Object.entries(data || {}).filter(
            ([key, value]) =>
                ALLOWED_DIRECTORY_USER_UPDATE_FIELDS.has(key) &&
                value !== null &&
                value !== undefined
        )
    );
    return apiFetch(`/api/v1/schools/${schoolId}/users/${userId}${query}`, {
        method: 'PATCH',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: JSON.stringify(cleanedData),
    });
};

/**
 * List school academic sessions.
 * @param {string} schoolId - School UUID
 * @param {Object} [params] - Query parameters
 * @param {boolean} [params.is_active]
 * @param {number} [params.skip]
 * @param {number} [params.limit]
 * @param {string} [token] - Optional access token
 */
export const getSchoolSessions = async (schoolId, params = {}, token) => {
    const searchParams = new URLSearchParams();
    if (params.is_active !== undefined) searchParams.append('is_active', params.is_active);
    if (params.skip !== undefined) searchParams.append('skip', params.skip);
    if (params.limit !== undefined) searchParams.append('limit', params.limit);

    const queryString = searchParams.toString();
    const endpoint = queryString ? `/api/v1/schools/${schoolId}/sessions?${queryString}` : `/api/v1/schools/${schoolId}/sessions`;

    return apiFetch(endpoint, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
};

/**
 * Create a school academic session.
 * @param {string} schoolId - School UUID
 * @param {Object} data - Session data
 * @param {string} [token] - Optional access token
 */
export const createSchoolSession = async (schoolId, data, token) => {
    return apiFetch(`/api/v1/schools/${schoolId}/sessions`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: JSON.stringify(data),
    });
};

/**
 * Update a school academic session.
 * @param {string} schoolId - School UUID
 * @param {string} sessionId - Session UUID
 * @param {Object} data - Session data updates
 * @param {string} [token] - Optional access token
 */
export const updateSchoolSession = async (schoolId, sessionId, data, token) => {
    return apiFetch(`/api/v1/schools/${schoolId}/sessions/${sessionId}`, {
        method: 'PATCH',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: JSON.stringify(data),
    });
};

/**
 * List school staff.
 * @param {string} schoolId - School UUID
 * @param {Object} [params] - Query parameters
 * @param {boolean} [params.is_active]
 * @param {string} [params.staff_role]
 * @param {number} [params.skip]
 * @param {number} [params.limit]
 * @param {string} [token] - Optional access token
 */
export const getSchoolStaff = async (schoolId, params = {}, token) => {
    let actualParams = params;
    let actualToken = token;
    if (typeof params === 'string') {
        actualToken = params;
        actualParams = {};
    }

    const searchParams = new URLSearchParams();
    if (actualParams.is_active !== undefined) searchParams.append('is_active', actualParams.is_active);
    if (actualParams.staff_role) searchParams.append('staff_role', actualParams.staff_role);
    if (actualParams.skip !== undefined) searchParams.append('skip', actualParams.skip);
    if (actualParams.limit !== undefined && actualParams.limit !== null) {
        const parsedLimit = parseInt(actualParams.limit, 10);
        const safeLimit = !isNaN(parsedLimit) && parsedLimit > 0 ? Math.min(parsedLimit, 100) : 100;
        searchParams.append('limit', String(safeLimit));
    }

    const queryString = searchParams.toString();
    const endpoint = queryString ? `/api/v1/schools/${schoolId}/staff?${queryString}` : `/api/v1/schools/${schoolId}/staff`;

    return apiFetch(endpoint, {
        method: 'GET',
        headers: actualToken ? { Authorization: `Bearer ${actualToken}` } : {},
    });
};

/**
 * Bulk create school staff.
 * @param {string} schoolId - School UUID
 * @param {Object} data - Contains items array of staff data
 * @param {string} [token] - Optional access token
 */
export const bulkCreateSchoolStaff = async (schoolId, data, token) => {
    return apiFetch(`/api/v1/schools/${schoolId}/staff/bulk`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: JSON.stringify(data),
    });
};

/**
 * List school enrollments.
 * @param {string} schoolId - School UUID
 * @param {Object} [params] - Query parameters
 * @param {string} [params.status]
 * @param {string} [params.session_id]
 * @param {number} [params.skip]
 * @param {number} [params.limit]
 * @param {string} [token] - Optional access token
 */
export const getSchoolEnrollments = async (schoolId, params = {}, token) => {
    const searchParams = new URLSearchParams();
    if (params.status) searchParams.append('status', params.status);
    if (params.session_id) searchParams.append('session_id', params.session_id);
    if (params.skip !== undefined) searchParams.append('skip', params.skip);
    if (params.limit !== undefined) searchParams.append('limit', params.limit);

    const queryString = searchParams.toString();
    const endpoint = queryString ? `/api/v1/schools/${schoolId}/enrollments?${queryString}` : `/api/v1/schools/${schoolId}/enrollments`;

    return apiFetch(endpoint, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
};

/**
 * Bulk create school enrollments.
 * @param {string} schoolId - School UUID
 * @param {Object} data - Contains items array of enrollment data
 * @param {string} [token] - Optional access token
 */
export const bulkCreateSchoolEnrollments = async (schoolId, data, token) => {
    return apiFetch(`/api/v1/schools/${schoolId}/enrollments/bulk`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: JSON.stringify(data),
    });
};

/**
 * Update a student enrollment.
 * @param {string} schoolId - School UUID
 * @param {string} enrollmentId - Enrollment UUID
 * @param {Object} data - grade_id, session_id, status
 * @param {string} [token] - Optional access token
 */
export const updateSchoolEnrollment = async (schoolId, enrollmentId, data, token) => {
    return apiFetch(`/api/v1/schools/${schoolId}/enrollments/${enrollmentId}`, {
        method: 'PATCH',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: JSON.stringify(data),
    });
};

/**
 * Update school staff membership.
 * @param {string} schoolId - School UUID
 * @param {string} staffId - Staff record UUID
 * @param {Object} data - staff_role, job_title, is_active
 * @param {string} [token] - Optional access token
 */
export const updateSchoolStaff = async (schoolId, staffId, data, token) => {
    return apiFetch(`/api/v1/schools/${schoolId}/staff/${staffId}`, {
        method: 'PATCH',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: JSON.stringify(data),
    });
};

/**
 * List academic terms for an academic session.
 * @param {string} schoolId - School UUID
 * @param {string} sessionId - Session UUID
 * @param {Object} [params] - Query parameters
 * @param {boolean} [params.is_active]
 * @param {number} [params.skip]
 * @param {number} [params.limit]
 * @param {string} [token] - Optional access token
 */
export const getSchoolSessionTerms = async (schoolId, sessionId, params = {}, token) => {
    const searchParams = new URLSearchParams();
    if (params.is_active !== undefined) searchParams.append('is_active', params.is_active);
    if (params.skip !== undefined) searchParams.append('skip', params.skip);
    if (params.limit !== undefined) searchParams.append('limit', params.limit);

    const queryString = searchParams.toString();
    const endpoint = queryString 
        ? `/api/v1/schools/${schoolId}/sessions/${sessionId}/terms?${queryString}` 
        : `/api/v1/schools/${schoolId}/sessions/${sessionId}/terms`;

    return apiFetch(endpoint, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
};

/**
 * Create an academic term within an academic session.
 * @param {string} schoolId - School UUID
 * @param {string} sessionId - Session UUID
 * @param {Object} data - Term data
 * @param {string} [token] - Optional access token
 */
export const createSchoolSessionTerm = async (schoolId, sessionId, data, token) => {
    return apiFetch(`/api/v1/schools/${schoolId}/sessions/${sessionId}/terms`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: JSON.stringify(data),
    });
};

/**
 * Update an academic term.
 * @param {string} schoolId - School UUID
 * @param {string} sessionId - Session UUID
 * @param {string} termId - Term UUID
 * @param {Object} data - Term data updates
 * @param {string} [token] - Optional access token
 */
export const updateSchoolSessionTerm = async (schoolId, sessionId, termId, data, token) => {
    return apiFetch(`/api/v1/schools/${schoolId}/sessions/${sessionId}/terms/${termId}`, {
        method: 'PATCH',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: JSON.stringify(data),
    });
};

/**
 * Deactivate/Delete an academic term.
 * @param {string} schoolId - School UUID
 * @param {string} sessionId - Session UUID
 * @param {string} termId - Term UUID
 * @param {string} [token] - Optional access token
 */
export const deleteSchoolSessionTerm = async (schoolId, sessionId, termId, token) => {
    return apiFetch(`/api/v1/schools/${schoolId}/sessions/${sessionId}/terms/${termId}`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
};

/**
 * List school term definitions (persistent catalog).
 * @param {string} schoolId - School UUID
 * @param {Object} [params] - Query parameters
 * @param {boolean} [params.is_active]
 * @param {number} [params.skip]
 * @param {number} [params.limit]
 * @param {string} [token] - Optional access token
 */
export const getSchoolTermDefinitions = async (schoolId, params = {}, token) => {
    const searchParams = new URLSearchParams();
    if (params.is_active !== undefined) searchParams.append('is_active', params.is_active);
    if (params.skip !== undefined) searchParams.append('skip', params.skip);
    if (params.limit !== undefined) searchParams.append('limit', params.limit);

    const queryString = searchParams.toString();
    const endpoint = queryString 
        ? `/api/v1/schools/${schoolId}/term-definitions?${queryString}` 
        : `/api/v1/schools/${schoolId}/term-definitions`;

    return apiFetch(endpoint, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
};

/**
 * Create a school term definition.
 * @param {string} schoolId - School UUID
 * @param {Object} data - Term definition data
 * @param {string} [token] - Optional access token
 */
export const createSchoolTermDefinition = async (schoolId, data, token) => {
    return apiFetch(`/api/v1/schools/${schoolId}/term-definitions`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: JSON.stringify(data),
    });
};

/**
 * Update a school term definition.
 * @param {string} schoolId - School UUID
 * @param {string} definitionId - Definition UUID
 * @param {Object} data - Term definition data updates
 * @param {string} [token] - Optional access token
 */
export const updateSchoolTermDefinition = async (schoolId, definitionId, data, token) => {
    return apiFetch(`/api/v1/schools/${schoolId}/term-definitions/${definitionId}`, {
        method: 'PATCH',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: JSON.stringify(data),
    });
};

/**
 * Deactivate/Delete a school term definition.
 * @param {string} schoolId - School UUID
 * @param {string} definitionId - Definition UUID
 * @param {string} [token] - Optional access token
 */
export const deleteSchoolTermDefinition = async (schoolId, definitionId, token) => {
    return apiFetch(`/api/v1/schools/${schoolId}/term-definitions/${definitionId}`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
};

/**
 * Permanently delete a school academic session.
 * @param {string} schoolId - School UUID
 * @param {string} sessionId - Session UUID
 * @param {string} [token] - Optional access token
 */
export const deleteSchoolSessionPermanent = async (schoolId, sessionId, token) => {
    return apiFetch(`/api/v1/schools/${schoolId}/sessions/${sessionId}/permanent`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
};

/**
 * Permanently delete an academic term within an academic session.
 * @param {string} schoolId - School UUID
 * @param {string} sessionId - Session UUID
 * @param {string} termId - Term UUID
 * @param {string} [token] - Optional access token
 */
export const deleteSchoolSessionTermPermanent = async (schoolId, sessionId, termId, token) => {
    return apiFetch(`/api/v1/schools/${schoolId}/sessions/${sessionId}/terms/${termId}/permanent`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
};

/**
 * Permanently delete a school term definition.
 * @param {string} schoolId - School UUID
 * @param {string} definitionId - Definition UUID
 * @param {string} [token] - Optional access token
 */
export const deleteSchoolTermDefinitionPermanent = async (schoolId, definitionId, token) => {
    return apiFetch(`/api/v1/schools/${schoolId}/term-definitions/${definitionId}/permanent`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
};
